import { getApiBaseUrl } from './api';

const getApiBase = () => getApiBaseUrl();

function getBookingAuthToken(): string | null {
  try {
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      if (session.expiresAt && Date.now() <= session.expiresAt) {
        return session.token || null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export interface GiftCardSummary {
  id: string;
  clubId: string;
  clubNumber: number | null;
  code: string | null;
  codeLast4: string;
  initialAmount: number;
  balance: number;
  currency: string;
  status: 'ACTIVE' | 'DEPLETED' | 'EXPIRED' | 'VOID';
  purchasedAt: string;
  expiresAt?: string | null;
  purchaserCustomerId?: string | null;
  recipientCustomerId?: string | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  message?: string | null;
  assignedAt?: string | null;
  passVersion?: number;
  scanPayload?: string;
  mobilePassAccessToken?: string;
}

export interface GiftCardEvent {
  id: string;
  giftCardId: string;
  type: string;
  amount: number;
  balanceAfter: number;
  memo?: string | null;
  createdAt: string;
}

export interface GiftCardDetail extends GiftCardSummary {
  events?: GiftCardEvent[];
}

class GiftCardsService {
  async getMyGiftCards(): Promise<GiftCardSummary[]> {
    const token = getBookingAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${getApiBase()}/gift-cards/me`, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}`);
    }
    const payload = await response.json();
    return Array.isArray(payload?.data) ? payload.data : [];
  }

  async getMyGiftCardById(id: string): Promise<GiftCardDetail> {
    const token = getBookingAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${getApiBase()}/gift-cards/me/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}`);
    }
    const payload = await response.json();
    return payload.data as GiftCardDetail;
  }
}

export const giftCardsService = new GiftCardsService();
